/**
 * Quote Detail
 *
 * Right panel showing selected quote details:
 * - Header with quote info
 * - Job list with routing visualization
 * - Team activity panel
 */

import JobRoutingRow from "./JobRoutingRow";

export default function QuoteDetail({
  quote,
  selectedJobs,
  onToggleJob,
  onSelectAllJobs,
  selectedWorkCenters,
  formatElapsed,
  onStartJob,
  onPauseJob,
  onResumeJob,
  onCompleteJob,
}) {
  if (!quote) return null;

  // Get jobs that need my work (for select all)
  const myJobs = quote.jobs.filter((j) => j.myOperations.length > 0);
  const selectableJobNumbers = myJobs
    .filter((j) => !j.activeSession)
    .map((j) => j.jobNumber);

  const allSelected =
    selectableJobNumbers.length > 0 &&
    selectableJobNumbers.every((jn) => selectedJobs.includes(jn));

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectAllJobs([]);
    } else {
      onSelectAllJobs(selectableJobNumbers);
    }
  };

  // Format due date
  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-slate-100">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {quote.projectName || "Unnamed Project"}
            </h2>
            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
              <span className="font-mono">{quote.quoteNumber}</span>
              {quote.customerCode && (
                <>
                  <span className="text-gray-600">|</span>
                  <span>{quote.customerCode}</span>
                </>
              )}
              {quote.projectManager && (
                <>
                  <span className="text-gray-600">|</span>
                  <span>PM: {quote.projectManager}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Due</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatDate(quote.dueDate)}
            </div>
          </div>
        </div>

        {/* Progress Summary */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex-1">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progress</span>
              <span>
                {quote.completedJobs} / {quote.totalJobs} jobs complete
              </span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{
                  width: `${
                    quote.totalJobs > 0
                      ? (quote.completedJobs / quote.totalJobs) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
          <div className="text-center px-3">
            <div className="text-2xl font-bold text-indigo-400">
              {quote.myPendingJobs}
            </div>
            <div className="text-xs text-gray-500">Need Me</div>
          </div>
        </div>
      </div>

      {/* Team Activity (if any) */}
      {quote.teamActivity && quote.teamActivity.length > 0 && (
        <div className="px-4 py-2 bg-slate-200 border-b border-gray-200">
          <div className="text-xs text-gray-500 mb-1">Team Activity</div>
          <div className="flex flex-wrap gap-2">
            {quote.teamActivity.map((activity, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-gray-700/50 rounded-full px-2 py-1 text-xs"
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    activity.state === "running"
                      ? "bg-green-500 animate-pulse"
                      : "bg-yellow-500"
                  }`}
                />
                <span className="text-gray-900">{activity.employeeName}</span>
                <span className="text-gray-500">
                  {activity.jobNumber} - {activity.operationCode}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Jobs Header */}
      <div className="px-4 py-2 flex items-center justify-between bg-slate-200 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
            disabled={selectableJobNumbers.length === 0}
            className="h-4 w-4 rounded border-gray-300 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-500">
            {selectedJobs.length > 0
              ? `${selectedJobs.length} selected`
              : "Select jobs"}
          </span>
        </div>
        <div className="text-xs text-gray-500">
          {quote.jobs.length} job{quote.jobs.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Job List */}
      <div className="flex-1 overflow-y-auto">
        {quote.jobs.map((job) => (
          <JobRoutingRow
            key={job.jobNumber}
            job={job}
            isSelected={selectedJobs.includes(job.jobNumber)}
            onToggle={() => onToggleJob(job.jobNumber)}
            selectedWorkCenters={selectedWorkCenters}
            formatElapsed={formatElapsed}
            onStart={onStartJob}
            onPause={onPauseJob}
            onResume={onResumeJob}
            onComplete={onCompleteJob}
            quoteNumber={quote.quoteNumber}
          />
        ))}
      </div>
    </div>
  );
}
